/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref } from 'vue'
import { HttpRequestMethod } from '~/constants/http'
import { CasinoPlayerType } from '~/enums/game'
import type { CasinoGameItemExtend, GameItemExtend } from '~/types/game'
import { useAPI } from '#lib/composables'
declare global {
  interface Window {
    NanoPlayer: any
    MillicastPlayer: any
    millicast: any
  }
}

export function useLivestreamGame({ game }: { game: CasinoGameItemExtend | GameItemExtend }) {
  const { SITE_HOST_NAME, RTMP_URL, WEBSOCKET_URL, STREAM_API_URL, HLS_URL } =
    useRuntimeConfig().public

  const SERVER_CONFIG = { websocket: WEBSOCKET_URL, hls: HLS_URL }
  const MAX_RETRIES = 1

  const NANO_CONFIG = {
    source: {
      entries: [
        {
          h5live: {
            rtmp: {
              url: RTMP_URL,
              streamname: '',
            },
            server: SERVER_CONFIG,
            security: {
              jwtoken: '',
            },
          },
        },
      ],
    },
    playback: {
      autoplay: true,
      playsinline: true,
      automute: true,
      muted: true,
      faststart: true,
      keepConnection: true,
    },
  }

  const retryCount = ref(0)
  const videoIdError = ref<Record<string, boolean>>({})
  const nanoConfig = {
    ...NANO_CONFIG,
    style: {
      width: game.width,
      height: game.height,
      controls: false,
      interactive: false,
      displayMutedAutoplay: false,
      fullScreenControl: false,
      scaling: 'fill',
      view: true,
    },
  }

  const isVideoLoaded = ref(false)
  const getStreamToken = async (
    game: CasinoGameItemExtend | GameItemExtend,
  ): Promise<string | undefined> => {
    const body = {
      groupId: game.id,
      streamId: game.key,
      site: SITE_HOST_NAME || window.location.hostname,
    }
    const keyCache = `${game.id ?? ''}_${game.id}_${Date.now()}`
    try {
      const { data } = await useAPI(STREAM_API_URL, {
        method: HttpRequestMethod.POST,
        body,
        key: keyCache.toString(),
        server: false,
      })
      const formatData = data?.value as any
      return formatData?.data?.token ?? ''
    } catch {
      return undefined
    }
  }

  const connectMillicastVideo = async (): Promise<void> => {
    const divId = `player-${game.gameId}`
    try {
      const videoEl = document.getElementById(divId)

      const streamName = game.key ?? ''
      const streamAccountId = game.id ?? ''
      const token = await getStreamToken(game)
      if(!token) {
        videoIdError.value = {
          [divId]: true
        }
        return
      }
      const tokenGenerator = (): void =>
        window.millicast.Director.getSubscriber({
          streamName,
          streamAccountId,
          subscriberToken: token,
        })

      await new window.millicast.View(
        streamName,
        tokenGenerator,
        videoEl,
      ).connect()
      isVideoLoaded.value = true
      videoIdError.value = {
        [divId]: false
      }
    } catch {
      videoIdError.value = {
        [divId]: true
      }
      isVideoLoaded.value = false
    }
  }

  const initNanoPlayer = async (): Promise<void> => {
    const divId = `player-${game.key}`
    const config = {
      ...nanoConfig,
      events: {
        onError: () => handleError(divId),
        onWebSocketError: () => handleWebSocketError(divId),
      },
    }
    try {
      const token = await getStreamToken(game)
      if (token) {
        config.source.entries[0].h5live.security.jwtoken = token
        config.source.entries[0].h5live.rtmp.streamname = game.key ?? ''
        const player = new window.NanoPlayer(divId)
        player.setup(config)
        videoIdError.value = {
          [divId]: false
        }
      }
    } catch {
      await retryInitialization()
    }
  }

  const retryInitialization = async (): Promise<void> => {
    if (retryCount.value <= MAX_RETRIES) {
      retryCount.value += 1
      if (game.playerType === CasinoPlayerType.Millicast) {
        await connectMillicastVideo()
      } else {
        await initNanoPlayer()
      }
    }
  }

  const handleError = (divId: string): void => {
    videoIdError.value = {
      [divId]: true
    }
    clearElements(divId)
    retryInitialization()
  }

  const handleWebSocketError = (divId: string): void => {
    videoIdError.value = {
      [divId]: true
    }
    clearElements(divId)
    retryInitialization()
  }

  const clearElements = (divId: string): void => {
    isVideoLoaded.value = false
    const element = document.getElementById(divId)
    videoIdError.value = {
      [divId]: true
    }
    if (element) {
      element.style.background = 'transparent'
      element.style.zIndex = '0'
    }

    const iframe = element?.querySelector('iframe')
    if (iframe) {
      iframe.style.display = 'none'
    }
  }

  const getPlayerKey = (divId: string) => `player-${divId}`

  const isShowError = (divId: string): boolean => {
    const playerKey = getPlayerKey(divId)
    return !!videoIdError.value[playerKey]
  }

  return {
    connectMillicastVideo,
    initNanoPlayer,
    isShowError
  }
}
